import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import WTypography from '../../../Components/whiteTypography'
import ConseillerChat from '../../ConseillerPatrimonial/components/ConseillerChat'
import { loadWallet } from '../../../Components/Billing/billingThunks'
import { makeGetAllRequest, makeGetRequest, makePostRequest } from 'utils/api'
import urls from 'utils/urls'

import {
  buildTimeAdvisor,
  timeBlockToEventPayload,
  quickAddToEventPayload, // LAP-765-v3
} from '../utils/buildTimeAdvisor'
import {
  TimeScoreCard,
  TimeDiagnosticPanel,
  TimeActionsPanel,
  TimeBlocksPanel,
  TimeBreakdownPanel, // LAP-765-v2
} from './TimeAdvisorSections'

/**
 * LAP-765 — Page principale "Mon conseiller temps IA".
 *
 * Pattern : strict miroir de `ConseillerPatrimonialPage`.
 *   - On agrège les données du profil utilisateur depuis plusieurs endpoints
 *     déjà existants : profil, événements, tâches, projets, objectifs,
 *     roadmap, sports, roue de vie / time profile.
 *   - On passe l'agrégat à `buildTimeAdvisor` qui produit score / diagnostic /
 *     actions / time-blocks / recommandations.
 *   - On expose un chat (réutilisation directe de `ConseillerChat` du Wealth
 *     Advisor) pour les questions libres.
 *
 * Coût en crédits : 1 crédit / message chat (feature `time_chat`).
 * Si le backend `/api/general/time-advisor/chat/` n'est pas exposé, on retombe
 * sur le fallback démo de ConseillerChat.
 *
 * Route : /conseiller-temps (cf. App.js).
 */
// LAP-765 — Prompts d'analyse par défaut si une carte SmartFeed pointe vers
// /conseiller-temps?periode=… SANS zone prompt BD renseignée. Dès qu'une carte
// a une `prompt_template`, celle-ci prime (passée via le state de navigation).
const PERIODE_DEFAULT_PROMPT = {
  week: "Fais-moi une analyse complète de ma semaine, domaine par domaine : ce qui a bien avancé, ce qui a pris du retard, et les 3 priorités concrètes pour la semaine à venir.",
  month: "Fais-moi une analyse complète de mon mois, domaine par domaine : tendances, points forts, points de vigilance, et les priorités pour le mois à venir.",
  quarter: "Fais-moi une analyse complète de mon trimestre, domaine par domaine : progression sur mes objectifs, ce qui décroche, et le plan d'action pour le trimestre suivant.",
  year: "Fais-moi une analyse complète de mon année, domaine par domaine : bilan global, réussites marquantes, angles morts, et les grands caps à tenir pour l'année à venir.",
}

// Normalise les alias de période (FR/EN) vers les clés canoniques.
const PERIODE_ALIASES = {
  week: 'week', semaine: 'week', hebdo: 'week', hebdomadaire: 'week',
  month: 'month', mois: 'month', mensuel: 'month',
  quarter: 'quarter', trimestre: 'quarter', trimestriel: 'quarter',
  year: 'year', annee: 'year', 'année': 'year', an: 'year', annuel: 'year',
}

function ConseillerTempsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation('layout')

  // LAP-765 — Amorçage « à l'instant T » depuis une carte SmartFeed d'analyse.
  //   - seedPrompt (state) = zone prompt de la BD (prompt_template), prioritaire.
  //   - ?periode= (query)  = repli : prompt d'analyse générique de la période.
  // Calculé une seule fois au montage pour éviter tout re-déclenchement.
  const [autoSendPrompt] = useState(() => {
    const seed = (location.state?.seedPrompt || '').trim()
    if (seed) return seed
    const rawPeriode = new URLSearchParams(location.search).get('periode') || ''
    const key = PERIODE_ALIASES[rawPeriode.toLowerCase().trim()]
    return key ? PERIODE_DEFAULT_PROMPT[key] : ''
  })

  const [chatOpen, setChatOpen] = useState(true)
  const [snackbar, setSnackbar] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(new Date())
  // LAP-765-v2 — ID du bloc en cours de programmation (UI feedback du bouton).
  const [schedulingId, setSchedulingId] = useState(null)
  // LAP-765-v3 — flag de chargement du formulaire d'ajout rapide.
  const [quickAdding, setQuickAdding] = useState(false)

  // Sources brutes
  const [rawSources, setRawSources] = useState(null)
  const siteUser = useSelector((s) => s.user)

  // ── Construction de l'advisor : recalculé à chaque changement de sources
  //    OU de langue (toutes les chaînes sont produites via `t`).
  const advisorData = useMemo(() => {
    if (!rawSources) return null
    return buildTimeAdvisor({ ...rawSources, t })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSources, t, i18n.language])

  const displayedFirstName =
    advisorData?.userProfile?.firstName ||
    siteUser?.user?.first_name ||
    siteUser?.user?.firstName ||
    siteUser?.user?.prenom ||
    ''

  // Scroll top à l'arrivée — sinon on hérite du scroll de la page précédente.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    fetchAllSources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * LAP-765 — Agrégation de toutes les sources Life nécessaires au conseiller
   * temps. Chaque requête est isolée (catch indépendant) car une seule absente
   * ne doit PAS faire tomber tout l'advisor. Si un endpoint n'existe pas
   * encore, on tombe sur tableau vide et l'advisor produit un diagnostic
   * partiel mais utilisable.
   *
   * Endpoints utilisés (déjà disponibles, cf. utils/urls.js + ils sont
   * référencés dans suggestions.py côté backend) :
   *   GET /api/user/user/                     → profil + centres d’intérêt
   *   GET /api/time/events/                   → événements calendrier BRUTS
   *   GET /api/mestodo/getTasks/              → tâches utilisateur
   *   GET /api/mestodo/project-operations/    → projets actifs
   *   GET /api/general/mesobjectifs/          → objectifs
   *   GET /api/roadmap/ideas/                 → idées de roadmap
   *   GET /api/general/wheel-of-life/         → roue de vie / time profile
   *   GET /api/health/sport-sessions/         → séances sport (peut ne pas
   *                                              exister, fallback []).
   *   GET /api/time/get-category-dd           → référentiel Time.Category
   *                                              (résolution des category_id).
   *
   * Si l'utilisateur veut une RE-GÉNÉRATION (handleRegenerate), on rappelle
   * ces endpoints + on incrémentera plus tard `?refresh=conseil&
   * feature=time_advisor_full` (5 crédits) côté backend dédié.
   */
  const fetchAllSources = async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) setRegenerating(true)
    else setInitialLoading(true)

    const safeGet = async (path) => {
      try {
        const r = await makeGetRequest(path, {}, true)
        return r?.data ?? null
      } catch (_) {
        return null
      }
    }

    // Variante « collection complète » : les listes DRF sont paginées à 20
    // (LimitOffsetPagination globale). Un simple makeGetRequest tronquait les
    // événements aux 20 PLUS ANCIENS (tri start_date ASC) — les séances de
    // sport récentes du calendrier n'arrivaient jamais jusqu'à l'advisor.
    const safeGetAll = async (path) => {
      try {
        const r = await makeGetAllRequest(path, {}, true)
        return r?.data ?? null
      } catch (_) {
        return null
      }
    }

    try {
      const [
        userData,
        events,
        tasks,
        projects,
        objectifs,
        roadmapIdeas,
        sportSessions,
        wheelOfLife,
        timeProfile,
        experiences, // LAP-765-v2 — emploi courant (MonParcoursPro)
        categories,
      ] = await Promise.all([
        safeGet('/api/user/user/'),
        // LAP-765-v3 — On lit les événements BRUTS (ModelViewSet, 1 ligne par
        // événement, champ `data` inclus) et non `/api/time/get-events` : ce
        // dernier déplie déjà les récurrences côté serveur. buildTimeAdvisor
        // les dépliait alors une 2e fois (expandOccurrences) → échéanciers
        // gravement sur-comptés dans la synthèse temps. Avec les lignes
        // brutes, expandOccurrences redevient l'unique source de dépliage.
        safeGetAll('/api/time/events/'),
        safeGetAll(urls.fetchTasks),
        safeGetAll(urls.projectOperations),
        safeGetAll(urls.crudMesObjectifs),
        safeGetAll(urls.roadmapIdeas),
        safeGet('/api/health/sport-sessions/'),
        safeGet('/api/general/wheel-of-life/'),
        safeGet('/api/general/time-profile/'),
        safeGetAll(`${urls.crudParcoursProOperations}?ParcoursType=Expérience`),
        // Référentiel Time.Category (non paginé) : les événements bruts
        // portent `category_id` en ENTIER ; sans ce référentiel,
        // buildTimeAdvisor ne peut pas résoudre le nom ("Sport", …) et tout
        // tombe en « Sans catégorie ».
        safeGet('/api/time/get-category-dd'),
      ])

      setRawSources({
        user: userData || siteUser?.user || {},
        events: extractList(events),
        categories: extractList(categories),
        tasks: extractList(tasks),
        projects: extractList(projects),
        objectifs: extractList(objectifs),
        roadmapIdeas: extractList(roadmapIdeas),
        sportSessions: extractList(sportSessions),
        wheelOfLife: wheelOfLife || {},
        timeProfile: timeProfile || {},
        experiences: extractList(experiences),
      })
      setLastUpdate(new Date())

      if (forceRefresh) {
        try {
          await dispatch(loadWallet())
        } catch (_) {
          /* silencieux */
        }
        setSnackbar({
          severity: 'success',
          message: t('conseillerTemps.regenerateSuccess', {
            defaultValue: 'Analyse temps régénérée.',
          }),
        })
      }
    } catch (e) {
      console.warn('[ConseillerTemps] fetch error', e?.message)
      if (!rawSources) {
        setRawSources({
          user: siteUser?.user || {},
          events: [],
          tasks: [],
          projects: [],
          objectifs: [],
          roadmapIdeas: [],
          sportSessions: [],
          wheelOfLife: {},
          timeProfile: {},
          experiences: [], // LAP-765-v2
          categories: [],
        })
      }
      if (forceRefresh) {
        setSnackbar({
          severity: 'error',
          message: t('conseillerTemps.regenerateError', {
            defaultValue: 'Impossible de régénérer l’analyse pour le moment.',
          }),
        })
      }
    } finally {
      if (forceRefresh) setRegenerating(false)
      else setInitialLoading(false)
    }
  }

  const handleRegenerate = () => {
    if (regenerating || initialLoading) return
    fetchAllSources({ forceRefresh: true })
  }

  /**
   * LAP-765-v2 — Programme un time-block dans l'agenda Life.
   *
   * On délègue le mapping bloc → payload à `timeBlockToEventPayload` (qui
   * gère la cadence → flags is_weekly/is_monthly/is_yearly + repeat_after,
   * la durée → start_time/end_time, le slot par défaut selon l'horizon).
   *
   * Après création (HTTP 201), on rafraîchit silencieusement les sources
   * pour que le nouvel événement (et toutes ses occurrences à venir) soit
   * immédiatement comptabilisé dans la charge hebdo, la répartition par
   * catégorie et le score. C'est le comportement attendu : si l'utilisateur
   * programme 3 séances de sport, son score "régularité sport" et son
   * "équilibre de vie" doivent monter au prochain rendu.
   */
  const handleScheduleBlock = async (block, horizon) => {
    if (!block || schedulingId) return
    setSchedulingId(block.id)
    try {
      const payload = timeBlockToEventPayload(block, horizon)
      const response = await makePostRequest('/api/time/events/', payload, {}, true)
      if (response?.status === 201 || response?.status === 200) {
        setSnackbar({
          severity: 'success',
          message: t('conseillerTemps.blocks.scheduledOk', {
            defaultValue: `« ${block.title} » ajouté à votre agenda.`,
            title: block.title,
          }),
        })
        // Re-fetch silencieux pour que le bloc compte tout de suite dans la
        // synthèse (récurrences incluses).
        fetchAllSources({ forceRefresh: false })
      } else {
        // LAP-765-v4 — On remonte la vraie cause de l'échec (ex. erreur de
        // validation DRF) au lieu du vague « réponse inattendue du serveur ».
        const detail = extractApiError(response)
        console.warn(
          '[ConseillerTemps] scheduleBlock rejected',
          response?.status,
          detail,
        )
        setSnackbar({
          severity: 'error',
          message: detail
            ? t('conseillerTemps.blocks.scheduledError', {
                defaultValue: `« ${block.title} » n’a pas pu être ajouté : ${detail}`,
                title: block.title,
                detail,
              })
            : t('conseillerTemps.blocks.scheduledMaybe', {
                defaultValue: `« ${block.title} » : réponse inattendue du serveur.`,
                title: block.title,
              }),
        })
      }
    } catch (e) {
      console.warn('[ConseillerTemps] scheduleBlock failed', e?.message)
      setSnackbar({
        severity: 'error',
        message: t('conseillerTemps.blocks.scheduledKo', {
          defaultValue: 'Impossible de programmer ce bloc pour le moment.',
        }),
      })
    } finally {
      setSchedulingId(null)
    }
  }

  /**
   * LAP-765-v3 — Ajout rapide d'un créneau personnalisé dans l'agenda.
   *
   * Reçoit la saisie brute du mini-formulaire (titre, catégorie, récurrence,
   * jours, date, heure, durée), la convertit en payload via
   * `quickAddToEventPayload`, poste sur /api/time/events/, puis rafraîchit
   * silencieusement les sources pour que le créneau et ses occurrences
   * comptent immédiatement dans la synthèse temps et la répartition.
   *
   * Renvoie `true` si l'événement a bien été créé (le formulaire vide alors
   * son champ titre), `false` sinon.
   */
  const handleQuickAdd = async (input) => {
    if (!input || !input.title || quickAdding) return false
    setQuickAdding(true)
    try {
      const payload = quickAddToEventPayload(input)
      const response = await makePostRequest('/api/time/events/', payload, {}, true)
      if (response?.status === 201 || response?.status === 200) {
        setSnackbar({
          severity: 'success',
          message: t('conseillerTemps.quickAdd.createdOk', {
            defaultValue: `« ${input.title} » ajouté à votre agenda.`,
            title: input.title,
          }),
        })
        // Re-fetch silencieux : le créneau compte aussitôt dans la synthèse.
        fetchAllSources({ forceRefresh: false })
        return true
      }
      // LAP-765-v4 — Détail de l'erreur serveur (validation DRF, etc.).
      const detail = extractApiError(response)
      console.warn('[ConseillerTemps] quickAdd rejected', response?.status, detail)
      setSnackbar({
        severity: 'error',
        message: detail
          ? t('conseillerTemps.quickAdd.createdError', {
              defaultValue: `« ${input.title} » n’a pas pu être ajouté : ${detail}`,
              title: input.title,
              detail,
            })
          : t('conseillerTemps.quickAdd.createdMaybe', {
              defaultValue: `« ${input.title} » : réponse inattendue du serveur.`,
              title: input.title,
            }),
      })
      return false
    } catch (e) {
      console.warn('[ConseillerTemps] quickAdd failed', e?.message)
      setSnackbar({
        severity: 'error',
        message: t('conseillerTemps.quickAdd.createdKo', {
          defaultValue: 'Impossible d’ajouter ce créneau pour le moment.',
        }),
      })
      return false
    } finally {
      setQuickAdding(false)
    }
  }

  /**
   * LAP-765 — Envoi d'un message au "Life Agent — Maître du Temps".
   * CONTRAT BACKEND attendu :
   *   POST /api/general/time-advisor/chat/
   *     body: { message, locale, history, feature: 'time_chat' }
   *     output: { reply, source, locale, generated_at }
   *     coût : 1 crédit, feature `time_chat`
   *     erreurs HTTP 402 → modale "Crédits insuffisants" globale (utils/api.js)
   *
   * Tant que l'endpoint n'existe pas, on relaie le rejet à ConseillerChat
   * qui utilise sa réponse de démo (setTimeout 1.2s).
   */
  const handleChatSend = async (message, locale) => {
    try {
      const response = await makePostRequest(
        '/api/general/time-advisor/chat/',
        {
          message,
          locale,
          feature: 'time_chat',
          // On envoie un contexte minimal pour aider le LLM à répondre.
          context: advisorData
            ? {
                metrics: advisorData.metrics,
                interests: advisorData.userProfile.interests,
                concerns: advisorData.userProfile.concerns,
                topRisks: advisorData.diagnostic.risks.map((r) => r.key),
              }
            : null,
        },
        {},
        true,
      )
      const data = response?.data || {}
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
      return {
        role: 'agent',
        content: data.reply || data.content || data.text || '',
        source: data.source || t('dashboard.lifeAgentSignature'),
        timestamp: data.generated_at || new Date().toISOString(),
        locale: data.locale || locale,
      }
    } catch (e) {
      console.warn('[ConseillerTemps] endpoint chat indispo, fallback démo', e?.message)
      throw e
    }
  }

  // ── Splash initial — comme le Wealth Advisor.
  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #00001d 0%, #0e1a36 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
          px: 4,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#7e9cdb' }} />
        <Box>
          <WTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('conseillerTemps.initialLoading', {
              defaultValue: 'Le maître du temps analyse votre agenda…',
            })}
          </WTypography>
          <WTypography
            variant="body2"
            sx={{ color: '#a0b4d6 !important', mt: 1, maxWidth: 480 }}
          >
            {t('conseillerTemps.initialLoadingHint', {
              defaultValue:
                'On agrège votre profil, votre calendrier, vos tâches, vos objectifs et votre roue de vie. Une trentaine de secondes maximum.',
            })}
          </WTypography>
        </Box>
      </Box>
    )
  }

  const initialChatMessages = [
    {
      role: 'agent',
      content: t('conseillerTemps.chat.welcome', {
        defaultValue: `Bonjour ${displayedFirstName || ''} 👋 Je suis votre maître du temps. Posez-moi n’importe quelle question sur l’organisation de votre semaine, de votre mois ou de votre année.`,
        name: displayedFirstName,
      }),
      source: t('dashboard.lifeAgentSignature'),
      timestamp: new Date().toISOString(),
    },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #00001d 0%, #0e1a36 100%)',
        color: 'white',
        pb: 6,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(0,0,29,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={() => navigate('/MonCalendrier')}
            sx={{ color: '#dbe5f7', flexShrink: 0 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <WTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: 14, sm: 16, md: 20 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('conseillerTemps.pageTitle', {
                defaultValue: 'Mon conseiller temps',
              })}
            </WTypography>
            <WTypography
              variant="caption"
              sx={{ color: '#a0b4d6 !important', display: { xs: 'none', md: 'block' } }}
            >
              {t('conseillerTemps.lastUpdated', {
                defaultValue: `Mis à jour ${formatRelativeI18n(lastUpdate, t)}`,
                date: formatRelativeI18n(lastUpdate, t),
              })}
            </WTypography>
          </Box>

          <IconButton
            onClick={handleRegenerate}
            disabled={regenerating}
            sx={{
              color: '#dbe5f7',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              '&.Mui-disabled': { color: 'rgba(219,229,247,0.4)' },
            }}
            aria-label={t('conseillerTemps.regenerate', {
              defaultValue: 'Régénérer l’analyse',
            })}
            title={t('conseillerTemps.regenerate', {
              defaultValue: 'Régénérer l’analyse',
            })}
          >
            <RestartAltIcon
              sx={{
                animation: regenerating ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>

          <IconButton
            onClick={() => setChatOpen(!chatOpen)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              flexShrink: 0,
              bgcolor: chatOpen ? '#3b82f6' : 'transparent',
              color: chatOpen ? 'white' : '#dbe5f7',
              border: chatOpen ? 'none' : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: chatOpen ? '#2563eb' : 'rgba(255,255,255,0.06)',
              },
            }}
            aria-label={
              chatOpen
                ? t('conseillerTemps.hideChat', { defaultValue: 'Masquer le chat' })
                : t('conseillerTemps.openChat', { defaultValue: 'Ouvrir le chat' })
            }
          >
            <ChatBubbleOutlineIcon />
          </IconButton>

          <Button
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={() => setChatOpen(!chatOpen)}
            variant={chatOpen ? 'contained' : 'outlined'}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              ...(chatOpen
                ? { bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }
                : { color: '#dbe5f7', borderColor: 'rgba(255,255,255,0.2)' }),
            }}
          >
            {chatOpen
              ? t('conseillerTemps.hideChat', { defaultValue: 'Masquer le chat' })
              : t('conseillerTemps.openChat', { defaultValue: 'Ouvrir le chat' })}
          </Button>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pt: 3 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            lg: chatOpen ? '2fr 1fr' : '1fr',
          }}
          gap={3}
          alignItems="flex-start"
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {advisorData && (
              <>
                <TimeScoreCard
                  score={advisorData.timeScore}
                  userFirstName={displayedFirstName}
                  // LAP-765-v2 — emploi + heures occupées passés au header.
                  employment={advisorData.employment}
                  metrics={advisorData.metrics}
                />
                <TimeDiagnosticPanel
                  diagnostic={advisorData.diagnostic}
                  recommendations={advisorData.recommendations}
                />
                {/* LAP-765-v2 — répartition du temps par catégorie sur
                    30 jours (occurrences récurrentes dépliées + heures
                    de travail synthétiques si emploi déclaré). */}
                <TimeBreakdownPanel breakdown={advisorData.timeBreakdown} />
                <TimeActionsPanel actions={advisorData.actionsPrioritaires} />
                <TimeBlocksPanel
                  blocks={advisorData.suggestedBlocks}
                  onSchedule={handleScheduleBlock}
                  schedulingId={schedulingId}
                  onQuickAdd={handleQuickAdd}
                  quickAdding={quickAdding}
                />
              </>
            )}
          </Box>

          {chatOpen && (
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 90 },
                alignSelf: 'flex-start',
                minWidth: 0,
              }}
            >
              <ConseillerChat
                messages={initialChatMessages}
                suggestedPrompts={advisorData?.suggestedPrompts || []}
                userLocale={i18n.language || 'fr-FR'}
                agentSignature={t('dashboard.lifeAgentSignature')}
                onSend={handleChatSend}
                autoSendPrompt={autoSendPrompt}
              />
            </Box>
          )}
        </Box>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

/**
 * LAP-765 — Normalise une réponse API qui peut être :
 *   - un Array direct
 *   - { results: [...] }
 *   - { data: [...] }
 *   - null
 */
function extractList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.data)) return payload.data
  return []
}

/**
 * LAP-765-v4 — Transforme une réponse d'erreur API en message lisible.
 *
 * `makePostRequest` renvoie `error.response` en cas d'échec : on dispose donc
 * du `status` ET du `data`. DRF renvoie soit `{ detail: "..." }`, soit un
 * dictionnaire `{ champ: ["message", ...], ... }` (erreurs de validation).
 * Cette fonction sert à remonter la vraie cause d'un échec d'ajout au
 * calendrier — par ex. « start_date : Date has wrong format » — au lieu du
 * message générique « réponse inattendue du serveur ».
 */
function extractApiError(response) {
  const data = response?.data
  if (!data) return ''
  if (typeof data === 'string') return data
  if (data.detail) return String(data.detail)
  try {
    return Object.entries(data)
      .map(([field, val]) => {
        const msg = Array.isArray(val) ? val.join(', ') : String(val)
        return `${field} : ${msg}`
      })
      .join(' · ')
  } catch (_) {
    return ''
  }
}

function formatRelativeI18n(date, t) {
  if (!date) return ''
  const now = Date.now()
  const then = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const diff = Math.max(0, now - then)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1)
    return t('conseillerTemps.relativeTime.justNow', { defaultValue: 'à l’instant' })
  if (minutes < 60)
    return t('conseillerTemps.relativeTime.minutesAgo', {
      defaultValue: `il y a ${minutes} min`,
      n: minutes,
    })
  if (hours < 24)
    return t('conseillerTemps.relativeTime.hoursAgo', {
      defaultValue: `il y a ${hours} h`,
      n: hours,
    })
  return t('conseillerTemps.relativeTime.daysAgo', {
    defaultValue: `il y a ${days} j`,
    n: days,
  })
}

export default ConseillerTempsPage
